import { DownOutlined } from '@ant-design/icons';
import { Alert, Col, Dropdown, Form, InputNumber, MenuProps, Modal, Row, Space, Spin } from 'antd';
import _ from 'lodash';
import objectHash from 'object-hash';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import ApplicationInterestMethodEnum from '~Api/Application/InterestMethodEnum';
import DealInterestMethodEnum from '~Api/Deal/DealInterestMethodEnum';
import MortgageTypeEnum from '~Api/Application/MortgageTypeEnum';
import ApplicationFeeFormatEnum from '~Api/Deal/ApplicationFeeFormatEnum';
import BrokerageFeeFormatEnum from '~Api/Deal/BrokerageFeeFormatEnum';
import EstablishmentFeeFormatEnum from '~Api/Deal/EstablishmentFeeFormatEnum';
import IProperty from '~Api/Deal/IProperty';
import ILoan from '~Api/Loan/ILoan';
import ILoanPayoutFigure from '~Api/Loan/ILoanPayoutFigure';
import {
    applicationsCreateRenewalFromLoanAction,
    applicationsCreateRenewalFromLoanErrorSetAction,
} from '~Applications/actions';
import {
    applicationsCreateRenewalFromLoanErrorsSelector,
    applicationsCreateRenewalFromLoanInProgressSelector,
    loanPayoutFigureApplicationSelector,
} from '~Applications/selectors';
import constants from '~constants';
import ICalculatorProperty from '~Deals/ICalculatorProperty';
import {
    DealCalculateBaseAmountEnum,
    IDealCalculateParameters,
    IDealCalculatedAmounts,
    getDealCalculatedAmounts,
} from '~Deals/utilities';
import {
    validateDealEstablishmentFee,
    validateDealInterestRate,
    validateDealLegalFees,
    validateDealMaximumLvr,
} from '~Leads/validators';
import { loanPayoutFigureGetWithLoanAndApplicationAction } from '~Loans/actions';
import {
    loanPayoutFigureDealPropertiesSelector,
    loanPayoutFigureLoanSelector,
    loanPayoutFigureSelector,
} from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import { currencyFormatter, percentageFormatter } from '~utilities/formatters';
import { IDictionary } from '~utilities/IDictionary';

const mortgageTypeLabels: IDictionary<string> = {
    [MortgageTypeEnum.FirstMortgage]: 'First Mortgage',
    [MortgageTypeEnum.SecondMortgage]: 'Second Mortgage',
};

const dealToApplicationInterestMethodMap: Record<DealInterestMethodEnum, ApplicationInterestMethodEnum> = {
    [DealInterestMethodEnum.InterestMethod360]: ApplicationInterestMethodEnum.InterestMethod360,
    [DealInterestMethodEnum.InterestMethod365]: ApplicationInterestMethodEnum.InterestMethod365,
};

interface IState {
    applicationFeeDollars: number;
    applicationFeeFormat: ApplicationFeeFormatEnum;
    applicationFeePercentage: number;
    baseAmount: DealCalculateBaseAmountEnum;
    brokerageFeeDollars: number;
    brokerageFeeFormat: BrokerageFeeFormatEnum;
    brokerageFeePercentage: number;
    calculateParametersHash: string;
    calculatedAmounts: IDealCalculatedAmounts;
    establishmentFeeDollars: number;
    establishmentFeeFormat: EstablishmentFeeFormatEnum;
    establishmentFeePercentage: number;
    estimatedOutlays: number;
    interestRate: number;
    legalFees: number;
    maximumLvr: number;
    requestedLoanAmount: number;
    requestedPayoutAmount: number;
    termMonths: number;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    loanPayoutFigureUuid: string;
}

interface IPropsSelector {
    application: IApplication;
    errors: IDictionary<string>;
    inProgress: boolean;
    loan: ILoan;
    payoutFigure: ILoanPayoutFigure;
    properties: IDictionary<IProperty>;
}

interface IPropsDispatch {
    applicationsCreateRenewalFromLoan: (
        applicationFee: number,
        applicationFeeFormat: ApplicationFeeFormatEnum,
        applicationFeePercentage: number,
        brokerageFee: number,
        brokerageFeeFormat: BrokerageFeeFormatEnum,
        brokerageFeePercentage: number,
        establishmentFee: number,
        establishmentFeeFormat: EstablishmentFeeFormatEnum,
        establishmentFeePercentage: number,
        estimatedOutlays: number,
        interestMethod: ApplicationInterestMethodEnum,
        interestRate: number,
        legalFees: number,
        loanAmount: number,
        lvr: number,
        maximumLvr: number,
        netBalance: number,
        termMonths: number,
    ) => void;
    payoutFigureGetWithLoanAndApplication: () => void;
    setError: (key: string, value: string) => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class CreateExtensionApplicationModal extends React.Component<Props, IState> {
    public state: IState = {
        applicationFeeDollars: null,
        applicationFeeFormat: ApplicationFeeFormatEnum.Percentage,
        applicationFeePercentage: null,
        baseAmount: DealCalculateBaseAmountEnum.RequestedPayoutAmount,
        brokerageFeeDollars: null,
        brokerageFeeFormat: BrokerageFeeFormatEnum.Percentage,
        brokerageFeePercentage: null,
        calculateParametersHash: null,
        calculatedAmounts: null,
        establishmentFeeDollars: null,
        establishmentFeeFormat: EstablishmentFeeFormatEnum.Percentage,
        establishmentFeePercentage: null,
        estimatedOutlays: null,
        interestRate: null,
        legalFees: null,
        maximumLvr: null,
        requestedLoanAmount: null,
        requestedPayoutAmount: null,
        termMonths: null,
    };

    private readonly debouncedCalculateDealAmounts: () => void = null;

    constructor(props: Props) {
        super(props);

        this.onChangeApplicationFeeDollars = this.onChangeApplicationFeeDollars.bind(this);
        this.onChangeApplicationFeePercentage = this.onChangeApplicationFeePercentage.bind(this);
        this.onClickBaseAmount = this.onClickBaseAmount.bind(this);
        this.onChangeBrokerageFeeDollars = this.onChangeBrokerageFeeDollars.bind(this);
        this.onChangeBrokerageFeePercentage = this.onChangeBrokerageFeePercentage.bind(this);
        this.onChangeEstablishmentFeeDollars = this.onChangeEstablishmentFeeDollars.bind(this);
        this.onChangeEstablishmentFeePercentage = this.onChangeEstablishmentFeePercentage.bind(this);
        this.onChangeEstimatedOutlays = this.onChangeEstimatedOutlays.bind(this);
        this.onChangeInterestRate = this.onChangeInterestRate.bind(this);
        this.onChangeLegalFees = this.onChangeLegalFees.bind(this);
        this.onChangeMaximumLvr = this.onChangeMaximumLvr.bind(this);
        this.onChangeRequestedLoanAmount = this.onChangeRequestedLoanAmount.bind(this);
        this.onChangeRequestedPayoutAmount = this.onChangeRequestedPayoutAmount.bind(this);
        this.onChangeTermMonths = this.onChangeTermMonths.bind(this);

        this.onClickApplicationFeeFormat = this.onClickApplicationFeeFormat.bind(this);
        this.onClickBrokerageFeeFormat = this.onClickBrokerageFeeFormat.bind(this);
        this.onClickCreateExtensionApplication = this.onClickCreateExtensionApplication.bind(this);
        this.onClickEstablishmentFeeFormat = this.onClickEstablishmentFeeFormat.bind(this);

        this.validateEstablishmentFee = this.validateEstablishmentFee.bind(this);
        this.validateInterestRate = this.validateInterestRate.bind(this);
        this.validateLegalFees = this.validateLegalFees.bind(this);
        this.validateMaximumLvr = this.validateMaximumLvr.bind(this);

        this.debouncedCalculateDealAmounts = _.debounce(this.calculateDealAmounts, 250);
    }

    public componentDidMount(): void {
        const { application, loan, payoutFigure, properties } = this.props;

        if (!application || !loan || !payoutFigure || !properties) {
            this.props.payoutFigureGetWithLoanAndApplication();
        }

        this.calculateDealAmounts();
    }

    public componentDidUpdate(): void {
        this.debouncedCalculateDealAmounts();
    }

    public render(): JSX.Element {
        const { application, errors, inProgress, isOpen, loan, payoutFigure, properties } = this.props;
        const { calculatedAmounts } = this.state;

        if (!application || !calculatedAmounts || !loan || !payoutFigure || !properties) {
            return (
                <Modal
                    destroyOnClose={true}
                    footer={false}
                    onCancel={this.props.onClose}
                    open={isOpen}
                    title='Create Extension Application'
                    width={1200}
                    wrapClassName='loan-create-extension-application-modal'
                >
                    <Spin />
                </Modal>
            );
        }

        const {
            applicationFeeDollars,
            applicationFeeFormat,
            applicationFeePercentage,
            baseAmount,
            brokerageFeeDollars,
            brokerageFeeFormat,
            brokerageFeePercentage,
            establishmentFeeDollars,
            establishmentFeeFormat,
            establishmentFeePercentage,
            estimatedOutlays,
            interestRate,
            legalFees,
            maximumLvr,
            requestedLoanAmount,
            requestedPayoutAmount,
            termMonths,
        } = this.state;

        const {
            applicationFeeTotal,
            brokerageFeeTotal,
            establishmentFeeTotal,
            grossBalanceOnSettlement,
            grossLoanAmount,
            interestPayable,
            defaultInterestRate,
            lvr,
            monthlyInterestPayments,
            monthlyInterestPaymentsDefault,
            netPrepaidBalanceOnSettlement,
            totalCostsDollars,
            totalCostsPercentage,
            totalCostsPercentagePerAnnum,
            totalValue,
        } = calculatedAmounts;

        const showCalculatedAmounts: boolean = !!interestRate && !!termMonths;

        const baseAmountMenu: MenuProps = {
            items: [
                {
                    key: 'base-amount',
                    label: baseAmount === DealCalculateBaseAmountEnum.GrossLoanAmount ? 'Requested Payout Amount' : 'Requested Loan Amount',
                    onClick: this.onClickBaseAmount,
                },
            ],
        };

        const baseAmountLabel: JSX.Element = (
            <Dropdown menu={baseAmountMenu}>
                <a>{baseAmount === DealCalculateBaseAmountEnum.RequestedPayoutAmount ? 'Requested Payout Amount' : 'Requested Loan Amount'} <DownOutlined /></a>
            </Dropdown>
        );

        const establishmentFeeMenu: MenuProps = {
            items: [
                {
                    key: 'establishment-fee-format',
                    label: establishmentFeeFormat === EstablishmentFeeFormatEnum.Percentage ? 'Dollar Value' : 'Percentage',
                    onClick: this.onClickEstablishmentFeeFormat,
                },
            ],
        };

        const establishmentFeeLabel: JSX.Element = (
            <Dropdown menu={establishmentFeeMenu}>
                <a>Renewal Fee <DownOutlined /></a>
            </Dropdown>
        );

        const applicationFeeMenu: MenuProps = {
            items: [
                {
                    key: 'application-fee-format',
                    label: applicationFeeFormat === ApplicationFeeFormatEnum.Percentage ? 'Dollar Value' : 'Percentage',
                    onClick: this.onClickApplicationFeeFormat,
                },
            ],
        };

        const applicationFeeLabel: JSX.Element = (
            <Dropdown menu={applicationFeeMenu}>
                <a>Application Fee <DownOutlined /></a>
            </Dropdown>
        );

        const brokerageFeeMenu: MenuProps = {
            items: [
                {
                    key: 'brokerage-fee-format',
                    label: brokerageFeeFormat === BrokerageFeeFormatEnum.Percentage ? 'Dollar Value' : 'Percentage',
                    onClick: this.onClickBrokerageFeeFormat,
                },
            ],
        };

        const brokerageFeeLabel: JSX.Element = (
            <Dropdown menu={brokerageFeeMenu}>
                <a>Brokerage Fee <DownOutlined /></a>
            </Dropdown>
        );

        const establishmentFeeDollarsMinimumLimit: number = _.min([ application.deal.establishmentFeeDollarsMinimumOverride, constants.APPLICATION_EXTENSION_ESTABLISHMENT_FEE_DOLLARS_MINIMUM ]);
        const establishmentFeePercentageMinimumLimit: number = _.min([ application.deal.establishmentFeePercentageMinimumOverride, constants.APPLICATION_EXTENSION_ESTABLISHMENT_FEE_PERCENTAGE_MINIMUM ]);
        const interestRateMinimumLimit: number = application.deal.interestRateMinimumOverride || (application.mortgageType === MortgageTypeEnum.SecondMortgage ? constants.DEAL_INTEREST_RATE_SECOND_MORTGAGE_MINIMUM : constants.DEAL_INTEREST_RATE_MINIMUM);
        const legalFeesDollarsMinimumLimit: number = application.deal.legalFeesDollarsMinimumOverride ?? constants.DEAL_LEGAL_FEES_DOLLARS_MINIMUM;
        const lvrMaximumLimit: number = application.deal.lvrMaximumOverride || constants.DEAL_LVR_MAXIMUM;

        // Handle API errors that are not tied to a specific form item
        const errorsWithoutFormItem: IDictionary<string> = _.omit(errors, [ 'establishmentFee', 'interestRate', 'legalFees', 'maximumLvr' ]);
        const errorsBlock: JSX.Element = _.keys(errorsWithoutFormItem).length > 0 && (
            <Alert
                className='form-error-message'
                description={<ul>{_.map(errorsWithoutFormItem, (error: string, index: string) => <li key={index}>{error}</li>)}</ul>}
                message='There was a problem creating the extension application:'
                showIcon={true}
                type='error'
            />
        );

        return (
            <Modal
                destroyOnClose={true}
                okButtonProps={{ danger: true, disabled: !!loan.extensionApplicationUuid, loading: inProgress }}
                okText='Create Extension Application'
                onCancel={this.props.onClose}
                onOk={this.onClickCreateExtensionApplication}
                open={isOpen}
                title='Create Extension Application'
                width={1200}
                wrapClassName='loan-create-extension-application-modal'
            >
                {errorsBlock}
                <Row>
                    <Col span={12}>
                        <Form.Item className='loan-amount-total' label={baseAmountLabel}>
                            {baseAmount === DealCalculateBaseAmountEnum.RequestedPayoutAmount && <InputNumber addonBefore='$' onChange={this.onChangeRequestedPayoutAmount} value={requestedPayoutAmount} />}
                            {baseAmount === DealCalculateBaseAmountEnum.GrossLoanAmount && <InputNumber addonBefore='$' onChange={this.onChangeRequestedLoanAmount} value={requestedLoanAmount} />}
                        </Form.Item>
                        <Form.Item className='term-months' label='Term'>
                            <InputNumber addonAfter='months' min={1} onChange={this.onChangeTermMonths} step={1} value={termMonths} />
                        </Form.Item>
                        <Form.Item className='interest-rate' help={errors.interestRate} label='Interest Rate' validateStatus={errors.interestRate && 'error'}>
                            <Space>
                                <InputNumber addonAfter='%' min={interestRateMinimumLimit} onBlur={this.validateInterestRate} onChange={this.onChangeInterestRate} step={0.1} value={interestRate} />
                                <span>({percentageFormatter.format(defaultInterestRate / 100)} default)</span>
                            </Space>
                        </Form.Item>
                        <Form.Item className='maximum-lvr' help={errors.maximumLvr} label='Maximum LVR' validateStatus={errors.maximumLvr && 'error'}>
                            <InputNumber addonAfter='%' max={lvrMaximumLimit} onBlur={this.validateMaximumLvr} onChange={this.onChangeMaximumLvr} value={maximumLvr} />
                        </Form.Item>
                        <Form.Item
                            className={establishmentFeeFormat === EstablishmentFeeFormatEnum.Dollars ? 'establishment-fee-dollars' : 'establishment-fee-percentage'}
                            help={errors.establishmentFee}
                            label={establishmentFeeLabel}
                            validateStatus={errors.establishmentFee && 'error'}
                        >
                            {establishmentFeeFormat === EstablishmentFeeFormatEnum.Dollars && <Space><InputNumber addonBefore='$' min={establishmentFeeDollarsMinimumLimit} onBlur={this.validateEstablishmentFee} onChange={this.onChangeEstablishmentFeeDollars} value={establishmentFeeDollars} /><span>({percentageFormatter.format(establishmentFeeTotal / grossLoanAmount)})</span></Space>}
                            {establishmentFeeFormat === EstablishmentFeeFormatEnum.Percentage && <Space><InputNumber addonAfter='%' min={establishmentFeePercentageMinimumLimit} onBlur={this.validateEstablishmentFee} onChange={this.onChangeEstablishmentFeePercentage} step={0.1} value={establishmentFeePercentage} /><span>({currencyFormatter.format(establishmentFeeTotal)})</span></Space>}
                        </Form.Item>
                        <Form.Item className={applicationFeeFormat === ApplicationFeeFormatEnum.Dollars ? 'application-fee-dollars' : 'application-fee-percentage'} label={applicationFeeLabel}>
                            {applicationFeeFormat === ApplicationFeeFormatEnum.Dollars && <Space><InputNumber addonBefore='$' addonAfter='+ GST' onChange={this.onChangeApplicationFeeDollars} value={applicationFeeDollars} /><span>({percentageFormatter.format(applicationFeeTotal / grossLoanAmount)})</span></Space>}
                            {applicationFeeFormat === ApplicationFeeFormatEnum.Percentage && <Space><InputNumber addonAfter='%' onChange={this.onChangeApplicationFeePercentage} step={0.1} value={applicationFeePercentage} /><span>({currencyFormatter.format(applicationFeeTotal)} + GST)</span></Space>}
                        </Form.Item>
                        <Form.Item className={brokerageFeeFormat === BrokerageFeeFormatEnum.Dollars ? 'brokerage-fee-dollars' : 'brokerage-fee-percentage'} label={brokerageFeeLabel}>
                            {brokerageFeeFormat === BrokerageFeeFormatEnum.Dollars && <Space><InputNumber addonBefore='$' addonAfter='+ GST' onChange={this.onChangeBrokerageFeeDollars} value={brokerageFeeDollars} /><span>({percentageFormatter.format(brokerageFeeTotal / grossLoanAmount)})</span></Space>}
                            {brokerageFeeFormat === BrokerageFeeFormatEnum.Percentage && <Space><InputNumber addonAfter='%' onChange={this.onChangeBrokerageFeePercentage} step={0.1} value={brokerageFeePercentage} /><span>({currencyFormatter.format(brokerageFeeTotal)} + GST)</span></Space>}
                        </Form.Item>
                        <Form.Item className='legal-fees' help={errors.legalFees} label='Legal Documents' validateStatus={errors.legalFees && 'error'}>
                            <InputNumber addonBefore='$' addonAfter='+ GST' min={legalFeesDollarsMinimumLimit} onBlur={this.validateLegalFees} onChange={this.onChangeLegalFees} value={legalFees} />
                        </Form.Item>
                        <Form.Item className='approximate-outlays' label='Approximate Outlays'>
                            <InputNumber addonBefore='$' onChange={this.onChangeEstimatedOutlays} value={estimatedOutlays} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item className='mortgage-type' label='Mortgage Type'>
                            {mortgageTypeLabels[application.mortgageType]}
                        </Form.Item>
                        <Form.Item className='gross-loan-amount' label='Gross Loan Amount'>
                            {showCalculatedAmounts ? currencyFormatter.format(grossLoanAmount) : '-'}
                        </Form.Item>
                        <Form.Item className='property-value' label='Property Value'>
                            {currencyFormatter.format(totalValue)}
                        </Form.Item>
                        <Form.Item className='current-debt' label='Current Debt (Payout Figure)'>
                            {currencyFormatter.format(payoutFigure.balanceAmount)}
                        </Form.Item>
                        <Form.Item className='lvr' label='LVR'>
                            {showCalculatedAmounts ? percentageFormatter.format(lvr / 100) : '-'}
                        </Form.Item>
                        <Form.Item className='interest-payable' label='Interest Payable'>
                            {showCalculatedAmounts ? currencyFormatter.format(interestPayable) : '-'}
                        </Form.Item>
                        <Form.Item className='monthly-interest-payments' label={application.deal.interestMethod === DealInterestMethodEnum.InterestMethod360 ? 'Monthly Interest Payments' : 'Monthly Interest (approx.)'}>
                            {showCalculatedAmounts ? currencyFormatter.format(monthlyInterestPayments) : '-'}  ({currencyFormatter.format(monthlyInterestPaymentsDefault)} default)
                        </Form.Item>
                        <Form.Item className='gross-balance-on-settlement' label='Gross Balance on Settlement'>
                            {showCalculatedAmounts ? currencyFormatter.format(grossBalanceOnSettlement) : '-'}
                        </Form.Item>
                        <Form.Item className='net-prepaid-balance-on-settlement' label='Net Balance on Settlement'>
                            {showCalculatedAmounts ? currencyFormatter.format(netPrepaidBalanceOnSettlement) : '-'}
                        </Form.Item>
                        <Form.Item className='total-costs-dollars' label='Total Costs'>
                            {showCalculatedAmounts ? currencyFormatter.format(totalCostsDollars) + ` (${percentageFormatter.format(totalCostsPercentage / 100)}, ${percentageFormatter.format(totalCostsPercentagePerAnnum / 100)} pa)` : '-'}
                        </Form.Item>
                    </Col>
                </Row>
            </Modal>
        );
    }

    private calculateDealAmounts(): void {
        const { application, payoutFigure, properties } = this.props;
        const { baseAmount } = this.state;

        if (!application || !properties) {
            return;
        }

        const calculateParameters: IDealCalculateParameters = _.omit(this.state, [ 'baseAmount', 'calculateParametersHash', 'calculatedAmounts' ]);

        const calculateParametersHash: string = objectHash(calculateParameters);

        if (calculateParametersHash !== this.state.calculateParametersHash) {
            const calculatorProperties: ICalculatorProperty[] = [];

            _.forEach(application.properties, (applicationProperty: IApplicationProperty) => {
                const property: ICalculatorProperty = { ...properties[applicationProperty.dealPropertyUuid] };

                if (!!applicationProperty.valuationValue) {
                    property.valuationValue = applicationProperty.valuationValue;
                }

                // Ignore property debt on extension applications
                property.currentDebt = 0;

                calculatorProperties.push(property);
            });

            const calculatedAmounts: IDealCalculatedAmounts = getDealCalculatedAmounts({
                ...calculateParameters,
                baseDealAmount: baseAmount,
                interestMethod: application.deal.interestMethod,
                isApplication: true,
                mortgageType: application.mortgageType,
                properties: calculatorProperties,
                totalCurrentDebt: payoutFigure.balanceAmount,
            });

            this.setState({
                calculateParametersHash,
                calculatedAmounts,
            });
        }
    }

    private onClickApplicationFeeFormat(): void {
        const { applicationFeeFormat } = this.state;

        this.setState({
            applicationFeeFormat: applicationFeeFormat === ApplicationFeeFormatEnum.Dollars ? ApplicationFeeFormatEnum.Percentage : ApplicationFeeFormatEnum.Dollars,
        });
    }

    private onClickBrokerageFeeFormat(): void {
        const { brokerageFeeFormat } = this.state;

        this.setState({
            brokerageFeeFormat: brokerageFeeFormat === BrokerageFeeFormatEnum.Dollars ? BrokerageFeeFormatEnum.Percentage : BrokerageFeeFormatEnum.Dollars,
        });
    }

    private onClickEstablishmentFeeFormat(): void {
        const { establishmentFeeFormat } = this.state;

        this.setState({
            establishmentFeeFormat: establishmentFeeFormat === EstablishmentFeeFormatEnum.Dollars ? EstablishmentFeeFormatEnum.Percentage : EstablishmentFeeFormatEnum.Dollars,
        });
    }

    private onChangeApplicationFeeDollars(value: number): void {
        this.setState({
            applicationFeeDollars: value,
        });
    }

    private onChangeApplicationFeePercentage(value: number): void {
        this.setState({
            applicationFeePercentage: value,
        });
    }

    private onClickBaseAmount(): void {
        const { baseAmount } = this.state;

        this.setState({
            baseAmount: baseAmount === DealCalculateBaseAmountEnum.GrossLoanAmount ? DealCalculateBaseAmountEnum.RequestedPayoutAmount : DealCalculateBaseAmountEnum.GrossLoanAmount,
        });
    }

    private onChangeBrokerageFeeDollars(value: number): void {
        this.setState({
            brokerageFeeDollars: value,
        });
    }

    private onChangeBrokerageFeePercentage(value: number): void {
        this.setState({
            brokerageFeePercentage: value,
        });
    }

    private onChangeEstablishmentFeeDollars(value: number): void {
        this.setState({
            establishmentFeeDollars: value,
        });
    }

    private onChangeEstablishmentFeePercentage(value: number): void {
        this.setState({
            establishmentFeePercentage: value,
        });
    }

    private onChangeEstimatedOutlays(value: number): void {
        this.setState({
            estimatedOutlays: value,
        });
    }

    private onChangeInterestRate(value: number): void {
        this.setState({
            interestRate: value,
        });
    }

    private onChangeLegalFees(value: number): void {
        this.setState({
            legalFees: value,
        });
    }

    private onChangeMaximumLvr(value: number): void {
        this.setState({
            maximumLvr: value,
        });
    }

    private onChangeRequestedLoanAmount(value: number): void {

        this.setState({
            requestedLoanAmount: value,
        });
    }

    private onChangeRequestedPayoutAmount(value: number): void {
        this.setState({
            requestedPayoutAmount: value,
        });
    }

    private onChangeTermMonths(value: number): void {
        this.setState({
            termMonths: value,
        });
    }

    private onClickCreateExtensionApplication(): void {
        const { application } = this.props;
        const {
            applicationFeeFormat,
            applicationFeePercentage,
            brokerageFeeFormat,
            brokerageFeePercentage,
            calculatedAmounts,
            establishmentFeeFormat,
            establishmentFeePercentage,
            estimatedOutlays,
            interestRate,
            legalFees,
            maximumLvr,
            termMonths,
        } = this.state;

        let valid: boolean = true;

        valid = this.validateEstablishmentFee() && valid;
        valid = this.validateInterestRate() && valid;
        valid = this.validateLegalFees() && valid;
        valid = this.validateMaximumLvr() && valid;

        if (!valid) {
            return;
        }

        const {
            applicationFeeTotal,
            brokerageFeeTotal,
            establishmentFeeTotal,
            grossLoanAmount,
            lvr,
            netPrepaidBalanceOnSettlement,
        } = calculatedAmounts;

        this.props.applicationsCreateRenewalFromLoan(
            applicationFeeTotal,
            applicationFeeFormat,
            applicationFeeFormat === ApplicationFeeFormatEnum.Percentage ? applicationFeePercentage : null,
            brokerageFeeTotal,
            brokerageFeeFormat,
            brokerageFeeFormat === BrokerageFeeFormatEnum.Percentage ? brokerageFeePercentage : null,
            establishmentFeeTotal,
            establishmentFeeFormat,
            establishmentFeeFormat === EstablishmentFeeFormatEnum.Percentage ? establishmentFeePercentage : null,
            estimatedOutlays,
            dealToApplicationInterestMethodMap[application.deal.interestMethod],
            interestRate,
            legalFees,
            grossLoanAmount,
            lvr,
            maximumLvr,
            netPrepaidBalanceOnSettlement,
            termMonths,
        );
    }

    private validateEstablishmentFee(): boolean {
        const { application } = this.props;
        const { calculatedAmounts } = this.state;

        const parameters: IDealCalculateParameters = { ...this.state };

        const { establishmentFeeTotal, grossLoanAmount } = calculatedAmounts;

        return validateDealEstablishmentFee({
            ...parameters,
            establishmentFeeDollarsMinimumOverride: _.min([ application.deal.establishmentFeeDollarsMinimumOverride, constants.APPLICATION_EXTENSION_ESTABLISHMENT_FEE_DOLLARS_MINIMUM ]),
            establishmentFeePercentageMinimumOverride: _.min([ application.deal.establishmentFeePercentageMinimumOverride, constants.APPLICATION_EXTENSION_ESTABLISHMENT_FEE_PERCENTAGE_MINIMUM ]),
            establishmentFeeTotal,
            grossLoanAmount,
            isBroker: (application.deal.isBroker || !!application.deal.brokerUuid),
        }, this.props.setError);
    }

    private validateInterestRate(): boolean {
        const { application } = this.props;

        const parameters: IDealCalculateParameters = { ...this.state };

        return validateDealInterestRate({
            ...parameters,
            interestRateMinimumOverride: application.deal.interestRateMinimumOverride,
            mortgageType: application.mortgageType,
        }, this.props.setError);
    }

    private validateLegalFees(): boolean {
        const { application } = this.props;

        const parameters: IDealCalculateParameters = { ...this.state };

        return validateDealLegalFees({
            ...parameters,
            legalFeesDollarsMinimumOverride: application.deal.legalFeesDollarsMinimumOverride,
        }, this.props.setError);
    }

    private validateMaximumLvr(): boolean {
        const { application } = this.props;
        const { calculatedAmounts } = this.state;

        const parameters: IDealCalculateParameters = { ...this.state };

        const { netPrepaidBalanceOnSettlement } = calculatedAmounts;

        return validateDealMaximumLvr({
            ...parameters,
            lvrMaximumOverride: application.deal.lvrMaximumOverride,
            netPrepaidBalanceOnSettlement,
        }, this.props.setError);
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        application: loanPayoutFigureApplicationSelector(state, ownProps.loanPayoutFigureUuid),
        errors: applicationsCreateRenewalFromLoanErrorsSelector(state, ownProps.loanPayoutFigureUuid),
        inProgress: applicationsCreateRenewalFromLoanInProgressSelector(state),
        loan: loanPayoutFigureLoanSelector(state, ownProps.loanPayoutFigureUuid),
        payoutFigure: loanPayoutFigureSelector(state, ownProps.loanPayoutFigureUuid),
        properties: loanPayoutFigureDealPropertiesSelector(state, ownProps.loanPayoutFigureUuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        applicationsCreateRenewalFromLoan: (
            applicationFee: number,
            applicationFeeFormat: ApplicationFeeFormatEnum,
            applicationFeePercentage: number,
            brokerageFee: number,
            brokerageFeeFormat: BrokerageFeeFormatEnum,
            brokerageFeePercentage: number,
            establishmentFee: number,
            establishmentFeeFormat: EstablishmentFeeFormatEnum,
            establishmentFeePercentage: number,
            estimatedOutlays: number,
            interestMethod: ApplicationInterestMethodEnum,
            interestRate: number,
            legalFees: number,
            loanAmount: number,
            lvr: number,
            maximumLvr: number,
            netBalance: number,
            termMonths: number,
        ) => dispatch(applicationsCreateRenewalFromLoanAction(
            applicationFee,
            applicationFeeFormat,
            applicationFeePercentage,
            brokerageFee,
            brokerageFeeFormat,
            brokerageFeePercentage,
            establishmentFee,
            establishmentFeeFormat,
            establishmentFeePercentage,
            estimatedOutlays,
            interestMethod,
            interestRate,
            legalFees,
            loanAmount,
            ownProps.loanPayoutFigureUuid,
            lvr,
            maximumLvr,
            netBalance,
            termMonths,
        )),
        payoutFigureGetWithLoanAndApplication: () => dispatch(loanPayoutFigureGetWithLoanAndApplicationAction(ownProps.loanPayoutFigureUuid)),
        setError: (key: string, value: string) => dispatch(applicationsCreateRenewalFromLoanErrorSetAction(ownProps.loanPayoutFigureUuid, key, value)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(CreateExtensionApplicationModal);
